import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import {
  DSButton,
  DSCard,
  DSIcon,
  DSTextInput,
  DSTypography,
  DsLogo,
  DsPageContainer
} from '@eiv2/design-system';
import { useAuth } from '../auth/useAuth';

/**
 * Login administrativo. As credenciais vão para `/api/auth/login` na MESMA
 * origem; o token nunca chega ao JavaScript — o servidor o guarda num cookie
 * HttpOnly. Por isso não há nada a salvar aqui.
 */
export function LoginPage() {
  const { admin, isChecking, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);

  if (isChecking) {
    return (
      <DsPageContainer className="grid min-h-dvh place-items-center bg-dark-light-mode-background">
        <div className="flex flex-col items-center gap-3 text-center">
          <span
            aria-hidden
            className="size-8 animate-spin rounded-full border-2 border-gray-3 border-t-brand-primary-3"
          />
          <div className="flex flex-col items-center gap-1">
            <DSTypography as="p" className="text-[13px] font-normal text-gray-5">
              Verificando sessão…
            </DSTypography>
            <DSTypography as="p" className="text-[13px] font-normal text-gray-5">
              Aguarde um instante
            </DSTypography>
          </div>
        </div>
      </DsPageContainer>
    );
  }
  if (admin) return <Navigate to="/dashboard" replace />;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível entrar.');
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = email.trim().length > 0 && password.length > 0 && !isSubmitting;

  return (
    <DsPageContainer className="flex min-h-dvh items-center justify-center bg-dark-light-mode-background p-4">
      <div className="flex w-full max-w-[420px] flex-col items-center gap-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <DsLogo className="h-10 w-12" />
          <DSTypography
            as="p"
            className="text-[12px] font-semibold uppercase tracking-[0.14em] text-gray-5"
          >
            EI Tracking
          </DSTypography>
          <DSTypography as="h1" className="text-[24px] font-bold text-dark-3">
            Painel administrativo
          </DSTypography>
        </div>

        <DSCard className="w-full rounded-[24px] p-6 shadow-[0_24px_80px_oklch(0.2101_0.0318_264.66_/_0.08)]">
          <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
            {error ? (
              <div
                className="rounded-[12px] bg-[oklch(0.5673_0.1828_24.16_/_0.1)] px-3 py-2"
                role="alert"
              >
                <DSTypography as="p" className="text-[13px] font-semibold text-feedback-danger">
                  {error}
                </DSTypography>
              </div>
            ) : null}

            <div className="flex flex-col gap-4">
              <DSTextInput
                autoComplete="username"
                autoFocus
                classNames={{
                  field: 'h-14 bg-dark-light-mode-background'
                }}
                icons={{
                  left: <DSIcon name="Sms" size={16} />
                }}
                label="E-mail"
                onChange={(e) => setEmail(e.target.value)}
                required
                type="email"
                value={email}
              />

              <DSTextInput
                autoComplete="current-password"
                classNames={{
                  field: 'h-14 bg-dark-light-mode-background'
                }}
                icons={{
                  right: (
                    <button
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      className="inline-flex cursor-pointer items-center justify-center text-gray-5 transition-colors hover:text-dark-3"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setShowPassword((current) => !current);
                      }}
                      type="button"
                    >
                      <DSIcon name={showPassword ? 'EyeSlash' : 'Eye'} size={20} />
                    </button>
                  )
                }}
                label="Sua senha"
                onChange={(e) => setPassword(e.target.value)}
                required
                type={showPassword ? 'text' : 'password'}
                value={password}
              />
            </div>

            <DSButton disabled={!canSubmit} size="prominent" type="submit">
              {isSubmitting ? 'Entrando…' : 'Entrar'}
            </DSButton>
          </form>
        </DSCard>
      </div>
    </DsPageContainer>
  );
}
